import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location} from '@angular/common';
import { Router } from '@angular/router';
import { Site } from 'src/app/models/site.model';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { LocalizationService } from '../internationalization/localization.service';
import { UtilsService } from 'src/app/services/util/util-service';
import { SiteService } from 'src/app/services/site/site.service';
import { SiteApiService } from 'src/app/services/site/site.api.service';
import { User } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth/auth.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { FormService } from 'src/app/services/form/form.service';
import { JobService } from 'src/app/services/job/job.service';
import { ClosedownService } from '@app/services/closedown/closedown.service';
import { Browser } from '@capacitor/browser';
import { GeolocationService } from '@app/services/geolocation/geolocation.service';
import { Job } from '@app/models/job.model';
import { ConfigurationService } from '@app/services/configuration/configuration.service';
import { CompanyType } from '@app/shared/enums';
import { UserService } from '@app/services/user/user.service';
import { OpenmapService } from '@app/services/openmap/openmap.service';
import * as L from 'leaflet';

@Component({
    selector: 'app-site-sign-in',
    templateUrl: './site-sign-in.component.html',
    styleUrls: ['./site-sign-in.component.scss'],
    standalone: false
})
export class SiteSignInComponent implements OnInit, OnDestroy {
  id = "";
  sites : Site[] | null = [];
  userLat : any = null;
  userLong : any = null;
  currentUser: User | undefined;
  withJob = false;
  withLink = false;
  locationMessage: string = '';
  jobs: Job[] = [];
  isSiteListJobCountEnable = false;
  isSiteDistanceDisplayEnable = false;
  isDebugModeActive: boolean = true;

  private map: L.Map | undefined;
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
  
  constructor(private _authService:AuthService,
    private _location: Location, 
    public _router: Router,
    private _siteService: SiteService,
    private _siteApiService: SiteApiService,
    private _loader: LoaderService,
    private _localizationService: LocalizationService,
    private _utilsService: UtilsService,
    private _formService: FormService,
    private _navigationService:NavigationService,
    private _jobService: JobService,
    private _closedownService : ClosedownService,
    private _geolocationService: GeolocationService,
    private _configurationService: ConfigurationService,
    private _userService: UserService,
    private _openMapService: OpenmapService) {
      this._navigationService.onChange(false, true);
  }

  async ngOnInit(): Promise<void> {
    this.isSiteListJobCountEnable = await this._configurationService.getIsSiteListJobCountEnable();
    this.isSiteDistanceDisplayEnable = await this._configurationService.getIsSiteDistanceDisplayEnable();
    this._authService.getUser().then(user => {
      this.currentUser = user;
      this.withJob = user.withJob;
    });
    await this.getUserDetails();
    await this.getGeoLocation();

  }

  private async getUserDetails(){
    this._userService.getUserByEmailWithDetails()
        .subscribe({
          next: (user) => {
            if(user){
              this.isDebugModeActive = user.isDebugModeActive;
            }
          },
          error: () => {}
        });
  }

  async initMap() {
    
    const mapId = this.mapContainer.nativeElement.id || 'map';

    // Initialize map
    this.map = await this._openMapService.initMap(mapId, this.userLat, this.userLong);

    if(this.sites && this.sites.length > 0){
      for (const site of this.sites) {
        if(site.lat && site.lon){
          var distances = this.calculateDistance(site.lat, site.lon);
         
          var contents = '<b>'  + site.name + '</b><br/>' + distances;
          if(this.isSiteListJobCountEnable){
            var jobCount = this.getJobCountForSite(site.id);
            if(jobCount > 0){
              contents += '<br/>' + jobCount + " " + this._localizationService.translate('sitesignin_lbl_jobavailabile');
            }
          }
            contents += '<br/><button id="view-jobs-' + site.id + '" class="map-button-continue">' + 
                this._localizationService.translate('sitesignin_btn_view_jobs') + '</button>';
       
          // Use the brand image URL for the marker
          const brandImageUrl = site.brand?.imageUrl;
          
          const siteMarker = this._openMapService.addMarker(
            site.lat, 
            site.lon, 
            contents, 
            "",
            brandImageUrl,
            'site'
          );

          siteMarker.on('popupopen', () => {
            setTimeout(() => {
              const jobButton = document.getElementById('view-jobs-' + site.id);
              if (jobButton) {
                jobButton.addEventListener('click', (e) => {
                  e.stopPropagation();
                   if (this.isSiteOnClickEnable(site)) {
                      this.siteSelectionOnclick(site);
                    }
                });
              }
            }, 50);
            });
          /*
          siteMarker.on('click', () => {
            if (this.isSiteOnClickEnable(site)) {
              this.siteSelectionOnclick(site);
            }
          });
          */
        }
      }
    }
  }

  private async getGeoLocation(){
    let latestLocation = this._geolocationService.getLatestLocation();
    if (latestLocation) {
      this.userLat = latestLocation.coords.latitude;
      this.userLong = latestLocation.coords.longitude;
      if(this.isSiteListJobCountEnable){
        this.getUserJobsforAllSites();
      }else{
        this.getNearbySite(this.userLat, this.userLong);
      }
    } else {
      this.locationMessage = this._localizationService.translate("sitesignin_device_location_turn_off");
    }
  }

  private getNearbySite(lat: number, long: number){
    this._siteApiService.getNearbySites(lat, long)
      .subscribe({
        next: sites => {
          this.sites = sites;
            if(this.isDebugModeActive){
              this.initMap();
            }
        },
        error: error => {
          if (error.error && error.error.errorCode) {
            alert(this._localizationService.translate(error.error.errorCode));
          }
          else {
            alert(this._localizationService.translate("sitesignin_error_get_sites"));
          }
        }
      });
  }

  calculateDistance(lat: number, long: number) : string{
    var distant = "-";
    if(!this._utilsService.isUndefinedOrNull(lat) && 
      !this._utilsService.isUndefinedOrNull(long) &&
      !this._utilsService.isUndefinedOrNull(this.userLat) && 
      !this._utilsService.isUndefinedOrNull(this.userLong)){
        
        var distantInNumber = this._utilsService.getDistanceBetweenTwoPointsinKM(lat, long, this.userLat, this.userLong);
  
        if (distantInNumber < 1) {
          distant = `${Math.round(distantInNumber * 1000)} meter(s)`;
        } else {
          distant = `${distantInNumber.toFixed(2)} km(s)`;
        }
    }
    return this._localizationService.translate('sitesignin_lbl_distance')
    .replace('{distant}', distant);
  }

  back(){
    this._location.back();
  }

  siteSelectionOnclick(site : Site){
    this._siteService.setSignedInSite(site).then(_ => {
      if(site.isInductionRequired){
        this.getSiteInductions(site);
      }else{
        this.getUserJobs(site);
      }
    });
  }

  getUserJobsforAllSites(){
    this._jobService.getJobsByCompanyRefNumber(undefined).subscribe({
      next : jobs => {
        if (jobs && jobs.length > 0 && this.currentUser) {
            this.jobs = jobs;
        }
      },
      error: error => {
        if (error.error && error.error.errorCode) {
          alert(this._localizationService.translate(error.error.errorCode));
        }
        else {
          alert(this._localizationService.translate("sitesignin_error_get_job"));
        }        
      },
      complete: () => {
        this.getNearbySite(this.userLat, this.userLong);
      }
    });
  }

  getUserJobs(site : Site){
    let userCompanies : string[] = [];
    if (this.currentUser){
      if (this.currentUser.companies){
        this.currentUser.companies.forEach(company => {
          if (company.companyType.toUpperCase() != CompanyType.SUBCON){
            userCompanies.push(company.referenceNumber);
          }
        });
      }

      this._jobService.getJobsByCompanyRefNumber(site.id).subscribe({
        next: jobs => {
          this._closedownService.setUserJobs(jobs);
          if (site.isInductionRequired){
            if (jobs && jobs.length > 0){
              this._router.navigate(['induction'], {
                queryParams :{
                  inductionSequence : 1
              }});
            }else{
              if (this.currentUser) {
                this.currentUser.withJob = false;
                this._authService.setUser(this.currentUser);
              }
              this._router.navigate(['signedinvisitor']);
            }
          }
          else{
            if(jobs && jobs.length > 0){
              this._closedownService.getProcessFlow().then(processFlow =>{
                this._loader.show();
                processFlow.siteCode = site?.code;
                
                this._closedownService.setProcessFlow(processFlow).then(cs =>{
                  this._closedownService.directToClosedown(false, undefined, this._closedownService.getSignInBackUrl());
                  this.reloadWhenBrowserClose();
                }).catch(e =>{  this._loader.hide();});

              }).catch(e =>{  this._loader.hide();});
            }else{
              alert(this._localizationService.translate("sitesignin_nocity_jobs"));
            }
          }
         
        },
        error: error => {
          if (error.error && error.error.errorCode) {
            alert(this._localizationService.translate(error.error.errorCode));
          }
          else {
            alert(this._localizationService.translate("sitesignin_error_get_job"));
          }        
          this._siteService.removeSignedInSite();
          this._router.navigate(['signin']);
        }
      });
    }
  }

  getSiteInductions(site : Site){
    this._formService.getByBrandOrSiteId(site.brand.id, site.id).subscribe({
      next : (form) => {
        if (form) {
          this._formService.setInductionConfig(form).then(r =>{
            this.getUserJobs(site);
          });
        }
        else {
          alert(this._localizationService.translate("induction_dialog_siteinduction_notexist"));
          this._siteService.removeSignedInSite();
          this._router.navigate(['signin']);
        }
      },
      error :  e => {
        if (e.error && e.error.errorCode) {
          alert(this._localizationService.translate(e.error.errorCode));
        }
        else {
          alert(this._localizationService.translate("induction_dialog_siteinduction_notexist"));
        }
        this._siteService.removeSignedInSite();
        this._router.navigate(['signin']);
      }
    });
  }

  reloadWhenBrowserClose(){
    if(this._utilsService.isMobileAndTablet()){
        Browser.addListener('browserFinished',() =>  {
          Browser.removeAllListeners().then(s=>{});
          location.replace('signin');
        });
    }
  }

  getJobCountForSite(siteId:string):number{
    var defaultCount = 0;

    if(siteId && this.jobs && this.jobs.length > 0){
      defaultCount = this.jobs.filter(s => s.siteId == siteId).length;
    }

    return defaultCount;
  }

  isSiteOnClickEnable(site:Site):boolean{
    var isEnable = true;
    if(site && !site.isInductionRequired){
      isEnable = this.getJobCountForSite(site.id) > 0;
    }
    return isEnable;
  }

  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
    }
  }
}